import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { intersectionWith, sortBy } from 'lodash'
import { Section } from '../../common/Section'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { FormGroup } from '../../form/FormGroup'
import { LineChart } from '../../common/charts/LineChart'
import { hoursInYear } from '../../../util/hours-in-year'
import { ChartHourSeriesSelect } from './ChartHourSeriesSelect'
import { formatPower, formatHour } from '../../../util/formatters'
import { ChartProps, EnergyWithName } from './use-chart-util'

const LEGEND_TICK_COUNT = 10

export function PowerDurationChart({
  titlePrefix,
  chartUtil,
}: ChartProps): React.ReactElement {
  const { t } = useTranslation()
  const { flowOptions, flows, defaultFlows } = chartUtil

  const [selectedFlows, setSelectedFlows] = useState<EnergyWithName[]>([])
  const [flowNames, setFlowNames] = useState<(EnergyType | string)[]>(
    defaultFlows.map((flow) => flow.name),
  )

  const handleFlowNamesChange = useCallback(
    (values: string[]) => {
      setFlowNames(values)

      const newFlows = intersectionWith(
        flows,
        values,
        (flow, name) => flow.name === name,
      )
      setSelectedFlows(newFlows)
    },
    [flows],
  )

  useEffect(() => {
    handleFlowNamesChange(defaultFlows.map((flow) => flow.name))
  }, [flowOptions, defaultFlows, handleFlowNamesChange])

  useEffect(() => {
    const newFlows = intersectionWith(
      flows,
      flowNames,
      (flow, name) => flow.name === name,
    )
    setSelectedFlows(newFlows)
  }, [flows, flowNames])

  const mapFlowToGraphData = (flow: EnergyWithName) => {
    const hourSeries =
      flow.hourSeries.length === 0
        ? (new Array(hoursInYear).fill(0) as number[])
        : flow.hourSeries

    const orderedValues = sortBy(hourSeries, (value) => -value)

    return {
      id: flow.name,
      data: orderedValues.map((value, index) => ({
        x: index,
        y: value,
      })),
    }
  }

  const data = selectedFlows.map(mapFlowToGraphData)

  const xValueFormatter = (index: number) =>
    t('charts.consumption.powerDuration.powerValueFormat', {
      count: index + 1,
    })

  const xAxisValueFormatter = (index: number) =>
    (index + 1) % Math.ceil(hoursInYear / LEGEND_TICK_COUNT) === 0
      ? formatHour(index + 1)
      : ''

  return (
    <Section
      title={`${titlePrefix}: ${t('charts.consumption.powerDuration.title')}`}
    >
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <FormGroup>
          <ChartHourSeriesSelect
            values={flowNames}
            onChange={handleFlowNamesChange}
            options={flowOptions}
          />
        </FormGroup>
      </Box>

      <Box width="100%" height={540} borderRadius={6}>
        {data.length > 0 && (
          <LineChart
            data={data}
            xFormatter={xValueFormatter}
            yFormatter={formatPower}
            xAxisFormatter={xAxisValueFormatter}
          />
        )}
      </Box>
    </Section>
  )
}
